'use client';

import { useEffect } from 'react';

/** Design reference cell size. The live cell size is responsive (see
 *  `cellForWidth`) and exposed at runtime via the `--grid-cell` CSS variable;
 *  `BASE_CELL` only fixes the proportions that `--grid-scale` (cell / BASE_CELL)
 *  scales the decorations against. */
export const BASE_CELL = 120;
/** Max width of the centered content/column band (logo→buttons, hero content).
 *  The grid cell size is independent of this — smaller cells just mean more
 *  columns inside the same band, so the navbar/content stay roomy. */
const MAX_CONTAINER = 1280;
/** Fallbacks used for SSR / the pre-hydration paint, before HeroGrid's effect
 *  sets the real vars. Must match the desktop branch of `cellForWidth`. */
const DEFAULT_CELL = 72;
const DEFAULT_SCALE = DEFAULT_CELL / BASE_CELL; // 0.6

/** Responsive grid cell size in px. Small enough that a single row reads as a
 *  navbar height. Shared by HeroGrid, Navbar and HeroDecorations so they all
 *  snap to the same coordinate system. */
export const cellForWidth = (w: number): number =>
  w < 640 ? 56 : w < 1024 ? 64 : 72;

/** The page-wide grid geometry for a given viewport width. The centered content
 *  band (`snapped`) is the largest whole number of cells that fits within
 *  MAX_CONTAINER; `offsetX` is the gap from the viewport's left edge to the
 *  band's left edge. Vertical body grid lines sit at `offsetX + n*cell` and
 *  extend across the FULL viewport, so columns can be negative (left margin) or
 *  run past the band (right margin). */
export const gridMetrics = (w: number) => {
  const cell = cellForWidth(w);
  const target = Math.min(w, MAX_CONTAINER);
  const snapped = Math.max(cell, Math.floor(target / cell) * cell);
  const offsetX = (w - snapped) / 2;
  return { cell, snapped, offsetX, scale: cell / BASE_CELL };
};

/** Horizontal/vertical position expressed in grid units. `cells` is a multiple
 *  of the live cell size; `px` is an absolute offset that scales with the grid
 *  (negative to subtract). Returns a `calc()` string so it tracks `--grid-cell`
 *  / `--grid-scale` without re-rendering. */
export const cellExpr = ({ cells = 0, px = 0 }: { cells?: number; px?: number }): string =>
  `calc(${cells} * var(--grid-cell, ${DEFAULT_CELL}px) + ${px}px * var(--grid-scale, ${DEFAULT_SCALE}))`;

/** A pixel length from the BASE_CELL design, scaled to the live cell size. */
export const scaleExpr = (px: number): string =>
  `calc(${px}px * var(--grid-scale, ${DEFAULT_SCALE}))`;

/** Left position of the vertical body grid line at global column `gcol`,
 *  measured from the viewport's left edge. `gcol` may be negative (left margin)
 *  or run past the content band (right margin) — that's how decorations reach
 *  the very edges of the screen while staying pinned to the page grid. */
export const gridX = (gcol: number, px = 0): string =>
  `calc(var(--grid-offset-x, ${DEFAULT_CELL}px) + ${gcol} * var(--grid-cell, ${DEFAULT_CELL}px) + ${px}px * var(--grid-scale, ${DEFAULT_SCALE}))`;

/**
 * Publishes the live grid geometry as CSS variables on the document root so the
 * page-wide body grid (app/globals.css), the navbar overlay and the hero
 * decoration field all stay locked to the same cells. Renders nothing itself —
 * the grid lines are painted at the body level and the decorations live in
 * HeroDecorations.
 */
const HeroGrid: React.FC = () => {
  useEffect(() => {
    // Debug aid: add `?grid` to the URL to render the alignment grid bold + blue
    // (see app/globals.css `html.grid-debug`) so cell borders around cleared
    // content are easy to verify. No effect on the normal page.
    document.documentElement.classList.toggle(
      'grid-debug',
      new URLSearchParams(window.location.search).has('grid'),
    );

    const update = () => {
      const w = document.documentElement.clientWidth;
      const { cell, snapped, offsetX } = gridMetrics(w);
      // Publish the live cell size + its scale relative to BASE_CELL so the body
      // grid and all decorations track it via var()/calc().
      document.documentElement.style.setProperty('--grid-cell', `${cell}px`);
      document.documentElement.style.setProperty('--grid-scale', `${cell / BASE_CELL}`);
      // Offset of the centered grid from the viewport's left edge. Drives both
      // the body background-position and gridX() for edge decorations.
      document.documentElement.style.setProperty('--grid-offset-x', `${offsetX}px`);
      document.documentElement.style.setProperty('--grid-container-width', `${snapped}px`);
      // Tailwind v4 doesn't pass var() through to background-position, so set
      // the body's position directly to ensure the grid actually shifts.
      document.body.style.backgroundPosition = `${offsetX}px 0`;
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return null;
};

export default HeroGrid;
